import type { ShakaPlayer } from '~/composables/useHlsPlayer/types';
import type { Ref } from 'vue';

import { useEventListener } from '@vueuse/core';
import { useDatadog } from '~~/composables/useDatadog';

// ---------------------------------------------------------------------------
// Video QoE instrumentation (Datadog RUM custom vitals + action).
//
// Market-standard metrics (UMass/Amherst-Akamai, Conviva):
//   - Startup time (VST): activation -> first frame. Good <2s, critical >5s.
//   - Rebuffering ratio: stall time / play time. Good <1%, critical >5%.
//   - Stall-out rate: % of plays with >=1 stall. Good <5%, critical >15%.
//   - EBVS: exit before video start.
//
// Emitted per play attempt:
//   vital  video_startup     duration = activation -> first frame
//   vital  video_ttff_click  duration = card click (markShortsOpenIntent) ->
//                            first frame — the user-felt TTFF on SPA opens
//   vital  video_ttff_nav    duration = document navigation -> first frame on
//                            hard loads (SEO/deep link) — includes SSR,
//                            hydration, player init and CDN. performance.now()
//                            is ms since navigation start, so the first-frame
//                            clock reading IS this metric.
//   vital  video_rebuffer    duration of each stall (one vital per stall)
//   vital  video_playtime    duration = media time actually played
//   action video_qoe         full summary incl. dropped frames (needs
//                            @context.* facets for slicing)
//
// Vitals only use standard facets (@vital.name, @vital.duration), so the
// dashboard works without any manual facet creation in Datadog.
// ---------------------------------------------------------------------------

export type VideoQoEPipeline = 'shaka' | 'ios_mse' | 'ios_native';

export type VideoQoEOutcome =
	| 'navigated' // attempt closed externally (scrolled away / unmounted)
	| 'ended'
	| 'error'
	| 'abandoned_before_start'; // EBVS: left before the first frame

export interface UseVideoQoEOptions {
	videoElement: Ref<HTMLVideoElement | null>;
	surface: string;
	pipeline: VideoQoEPipeline;
	getVideoUrl: () => string | null;
	getShakaPlayer?: () => ShakaPlayer | null;
	/** Whether this URL was prefetched by user intent — measures the warmup deploys. */
	wasWarmed?: (url: string) => boolean;
}

export interface UseVideoQoEReturn {
	/** Call when the video becomes the active/playing one (user intent). Idempotent while an attempt is open. */
	beginAttempt: () => void;
	/** Call when the play session ends (deactivated, unmounted, ended, error). */
	finalize: (outcome?: VideoQoEOutcome) => void;
	/**
	 * Discard the open attempt without emitting anything. For cases where the
	 * clock is no longer honest — e.g. autoplay blocked: startup would include
	 * the user's think-time until the tap, which is not player performance.
	 */
	abort: () => void;
}

/** Stalls shorter than this are render blips, not user-perceived rebuffering. */
const MIN_STALL_MS = 250;
/** A 'waiting' right after a seek is seek latency, not a network stall. */
const SEEK_GRACE_MS = 500;
/** timeupdate deltas above this are seeks/loop-wraps, not continuous playback. */
const MAX_PLAY_DELTA_S = 2;
/**
 * `HTMLMediaElement.HAVE_CURRENT_DATA` as a literal: reading the static off the
 * global yields undefined in some engines/test environments, and comparing a
 * readyState against undefined is always false — which would silently disable
 * the whole fallback first-frame path.
 */
const HAVE_CURRENT_DATA = 2;

// ---------------------------------------------------------------------------
// Page-scoped intent clock (module state, one page = one document lifetime).
// ---------------------------------------------------------------------------

/**
 * A click stamp older than this is stale — the navigation it announced never
 * produced a play (aborted route, user wandered off). Consuming it would
 * report minutes as TTFF.
 */
const INTENT_MAX_AGE_MS = 15_000;

/**
 * The hard-load TTFF clock is `performance.now()`, i.e. time since the document
 * navigation. A play that only starts long after the page loaded (the user
 * scrolled around, then hit play) would report the whole visit as
 * time-to-first-frame. Past this age the reading is no longer a TTFF.
 */
const NAV_TTFF_MAX_MS = 60_000;

let openIntentAt: number | null = null;
/**
 * The hard-load TTFF is a single reservation per document: only the first
 * play of the page that was server-rendered may claim it.
 */
let navTtffReservation = true;

/**
 * Stamp the moment the user clicks something that opens the shorts feed —
 * the start of the user-felt time-to-first-frame (video_ttff_click).
 */
export function markShortsOpenIntent(): void {
	if (!import.meta.client) return;
	openIntentAt = performance.now();
}

/** Test/page-lifecycle helper: clears the module-scoped clocks. */
export function resetVideoQoEPageState(): void {
	openIntentAt = null;
	navTtffReservation = true;
}

/**
 * Whether THIS player belongs to the server-rendered document, i.e. the user
 * landed here directly (SEO/deep link) rather than navigating in the SPA.
 *
 * Counting router navigations is not enough: a lazily-loaded player only
 * installs its hook on the destination route, so an SPA entry with no click
 * stamp would look like a landing and report the time since the PREVIOUS
 * document load. `isHydrating` is true only while the initial render happens,
 * which is exactly the condition we need.
 */
function mountedDuringHydration(): boolean {
	if (!import.meta.client) return false;
	try {
		return Boolean(useNuxtApp().isHydrating);
	} catch {
		// No Nuxt context (unit tests): never claim a hard load.
		return false;
	}
}

interface Attempt {
	id: string;
	url: string;
	startedAt: number;
	firstFrameAt: number | null;
	startupMs: number | null;
	stallStartedAt: number | null;
	stallCount: number;
	rebufferMs: number;
	lastSeekAt: number;
	variantHeight: number | null;
	warmed: boolean;
	/** Click stamp consumed from markShortsOpenIntent, if this play came from one. */
	intentAt: number | null;
	/** Eligible for the once-per-page hard-load TTFF (no SPA navigation yet). */
	navEligible: boolean;
	/** Same video restarted after 'ended' — warm startup vitals are skipped. */
	isReplay: boolean;
	/** Pending requestVideoFrameCallback handle, cancelled when the attempt closes. */
	frameCallbackHandle: number | null;
	// Accumulated on timeupdate instead of read at finalize: on iOS the shared
	// <video> may already belong to the NEXT reel when this attempt closes, and
	// reading element state then would attribute the wrong video's playback.
	// Deltas of currentTime (not video.played, which is a UNION of ranges and
	// caps at the video duration on looped/replayed playback).
	playedSeconds: number;
	lastMediaTime: number | null;
	durationSeconds: number | null;
	// getVideoPlaybackQuality counters are per-element (shared/reused elements
	// accumulate across videos) — baseline at first frame, delta per play.
	framesBaseline: { dropped: number; total: number } | null;
	droppedFrames: number;
	totalFrames: number;
}

export function useVideoQoE(options: UseVideoQoEOptions): UseVideoQoEReturn {
	const { addCustomVital } = useDatadog();
	// Evaluated at setup: only a player rendered with the document can be the
	// landing play. Mounting later (SPA route change) makes this false.
	const isLandingPlayer = mountedDuringHydration();

	let attempt: Attempt | null = null;
	let lastEndedUrl: string | null = null;

	function now(): number {
		return performance.now();
	}

	function baseContext(a: Attempt): Record<string, unknown> {
		return {
			play_id: a.id,
			surface: options.surface,
			pipeline: options.pipeline,
			video_url: a.url
		};
	}

	function activeVariantHeight(): number | null {
		const player = options.getShakaPlayer?.();
		if (!player) return null;
		try {
			const active = player.getVariantTracks().find(t => t.active);
			return active?.height ?? null;
		} catch {
			return null;
		}
	}

	function beginAttempt(): void {
		if (!import.meta.client) return;
		const url = options.getVideoUrl()?.trim();
		if (!url) return;
		// Same attempt still running for the same video — keep measuring.
		if (attempt && attempt.url === url) return;
		if (attempt) finalize('navigated');

		const isReplay = url === lastEndedUrl;
		// The replay flag is only valid for the play right after the 'ended';
		// a different video in between makes any later return a genuine play.
		lastEndedUrl = null;

		// The click stamp announces a FEED open — only the feed may read it (a
		// carousel card on the departing page could otherwise steal it during
		// the route transition), and only while fresh.
		//
		// Reading does NOT clear it. The feed opens with the clicked reel, and
		// the virtualized list can swap `videoUrl` on the live component before
		// anything paints: that closes this attempt and opens another. Clearing
		// on read burned the stamp on the discarded attempt, so the play that
		// actually rendered reported no click time at all. The stamp is cleared
		// when a ttff_click is really emitted, and expires on its own by TTL.
		let intentAt: number | null = null;
		if (
			options.surface === 'shorts_feed' &&
			openIntentAt !== null &&
			now() - openIntentAt <= INTENT_MAX_AGE_MS
		) {
			intentAt = openIntentAt;
		}

		// Claim the hard-load reservation now, not at first frame: if this play
		// never renders, the reservation must NOT roll over to the next reel
		// (its clock would still start at the document navigation).
		//
		// `isLandingPlayer` is read at setup, so a player created with the
		// document but only played minutes later would still look like a
		// landing play and report the whole session as time-to-first-frame.
		// The clock is only a TTFF while it is still plausibly one.
		const navEligible =
			isLandingPlayer &&
			intentAt === null &&
			navTtffReservation &&
			now() <= NAV_TTFF_MAX_MS;
		if (navEligible) navTtffReservation = false;

		attempt = {
			id:
				Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8),
			url,
			startedAt: now(),
			firstFrameAt: null,
			startupMs: null,
			stallStartedAt: null,
			stallCount: 0,
			rebufferMs: 0,
			lastSeekAt: 0,
			variantHeight: null,
			warmed: options.wasWarmed?.(url) ?? false,
			intentAt,
			navEligible,
			isReplay,
			frameCallbackHandle: null,
			playedSeconds: 0,
			lastMediaTime: null,
			durationSeconds: null,
			framesBaseline: null,
			droppedFrames: 0,
			totalFrames: 0
		};

		armFrameConfirmation();
	}

	function abort(): void {
		if (attempt) cancelFrameConfirmation(attempt);
		attempt = null;
	}

	// -------------------------------------------------------------------------
	// First-frame confirmation.
	//
	// `playing` only means playback can continue — it fires before anything is
	// painted, so using it as the first frame reports a startup that is too
	// optimistic. requestVideoFrameCallback runs when a frame has actually been
	// presented for composition, which is the metric's definition. Firefox has
	// no rVFC; there the guarded `timeupdate` (currentTime > 0, not paused,
	// readyState >= HAVE_CURRENT_DATA) remains the fallback.
	// -------------------------------------------------------------------------

	type VideoWithFrameCallback = HTMLVideoElement & {
		requestVideoFrameCallback?: (callback: () => void) => number;
		cancelVideoFrameCallback?: (handle: number) => void;
	};

	function supportsFrameCallback(video: HTMLVideoElement): boolean {
		return (
			typeof (video as VideoWithFrameCallback).requestVideoFrameCallback ===
			'function'
		);
	}

	function armFrameConfirmation(): void {
		const a = attempt;
		const video = options.videoElement.value as VideoWithFrameCallback | null;
		if (!a || !video || a.firstFrameAt !== null) return;
		if (a.frameCallbackHandle !== null) return;
		if (!supportsFrameCallback(video)) return;

		try {
			a.frameCallbackHandle = video.requestVideoFrameCallback(() => {
				a.frameCallbackHandle = null;
				// A callback armed by a superseded attempt must never mark the
				// current one — the shared element can outlive the attempt.
				if (attempt === a) onFirstFrame();
			});
		} catch {
			// Fallback path stays available.
		}
	}

	function cancelFrameConfirmation(a: Attempt): void {
		const video = options.videoElement.value as VideoWithFrameCallback | null;
		if (a.frameCallbackHandle === null) return;
		try {
			video?.cancelVideoFrameCallback?.(a.frameCallbackHandle);
		} catch {
			// Handle already consumed or element detached.
		}
		a.frameCallbackHandle = null;
	}

	function onFirstFrame(): void {
		if (!attempt || attempt.firstFrameAt !== null) return;
		cancelFrameConfirmation(attempt);
		attempt.firstFrameAt = now();
		attempt.startupMs = Math.round(attempt.firstFrameAt - attempt.startedAt);
		attempt.variantHeight = activeVariantHeight();

		// Replays restart from warm cache — their near-zero "startup" would
		// dilute the real cold/warm startup distributions. The attempt still
		// tracks stalls/playtime; the summary action carries replay=true.
		if (attempt.isReplay) {
			captureFramesBaseline();
			return;
		}

		addCustomVital('video_startup', {
			duration: attempt.startupMs,
			description: 'Video startup time (activation to first frame)',
			context: {
				...baseContext(attempt),
				warmed: attempt.warmed,
				variant_height: attempt.variantHeight
			}
		});

		// User-felt TTFF: card click -> first frame (SPA feed open). The stamp is
		// claimed here, not at read time, so only a play that actually painted
		// consumes it. Reading is shared (several players may hold the same
		// stamp), claiming is exclusive: whoever paints first wins and clears
		// it, and the others find it gone and stay quiet.
		if (attempt.intentAt !== null && openIntentAt === attempt.intentAt) {
			openIntentAt = null;
			addCustomVital('video_ttff_click', {
				duration: Math.round(attempt.firstFrameAt - attempt.intentAt),
				description: 'Time from feed-opening click to first video frame',
				context: baseContext(attempt)
			});
		} else if (attempt.navEligible) {
			// Hard-load TTFF: performance.now() is ms since navigation start, so
			// the first-frame clock reading covers SSR + hydration + player + CDN.
			addCustomVital('video_ttff_nav', {
				duration: Math.round(attempt.firstFrameAt),
				description:
					'Time from document navigation to first video frame (hard load)',
				context: baseContext(attempt)
			});
		}

		captureFramesBaseline();
	}

	// Dropped-frame baseline: the element's counters predate this play.
	function captureFramesBaseline(): void {
		if (!attempt) return;
		const video = options.videoElement.value;
		if (video && typeof video.getVideoPlaybackQuality === 'function') {
			try {
				const q = video.getVideoPlaybackQuality();
				attempt.framesBaseline = {
					dropped: q.droppedVideoFrames,
					total: q.totalVideoFrames
				};
			} catch {
				// Quality API unavailable — dropped-frame fields stay zero.
			}
		}
	}

	function closeStall(a: Attempt): void {
		if (a.stallStartedAt === null) return;
		const stallMs = Math.round(now() - a.stallStartedAt);
		a.stallStartedAt = null;

		if (stallMs < MIN_STALL_MS) return;
		a.stallCount++;
		a.rebufferMs += stallMs;

		addCustomVital('video_rebuffer', {
			duration: stallMs,
			description: 'Video rebuffering stall duration',
			context: baseContext(a)
		});
	}

	function finalize(outcome: VideoQoEOutcome = 'navigated'): void {
		if (!attempt) return;
		const a = attempt;
		attempt = null;
		cancelFrameConfirmation(a);
		closeStall(a);

		if (outcome === 'ended') lastEndedUrl = a.url;

		const playMs = Math.round(a.playedSeconds * 1000);
		const watchedPct =
			a.durationSeconds && a.durationSeconds > 0
				? Math.min(100, Math.round((a.playedSeconds / a.durationSeconds) * 100))
				: null;

		const finalOutcome: VideoQoEOutcome =
			a.firstFrameAt === null && outcome !== 'error'
				? 'abandoned_before_start'
				: outcome;

		if (playMs > 0) {
			addCustomVital('video_playtime', {
				duration: playMs,
				description: 'Media time actually played',
				context: baseContext(a)
			});
		}

		try {
			window.DD_RUM?.addAction('video_qoe', {
				...baseContext(a),
				outcome: finalOutcome,
				warmed: a.warmed,
				variant_height: a.variantHeight,
				// Which TTFF branch this play took. Without these, a missing
				// vital is indistinguishable from a play that legitimately had
				// no click stamp and was not a landing play.
				ttff_kind:
					a.intentAt !== null ? 'click' : a.navEligible ? 'nav' : 'none',
				startup_ms: a.startupMs,
				rebuffer_ms: a.rebufferMs,
				stall_count: a.stallCount,
				stalled: a.stallCount > 0,
				replay: a.isReplay,
				play_ms: playMs,
				watched_pct: watchedPct,
				dropped_frames: a.droppedFrames,
				total_frames: a.totalFrames,
				dropped_pct:
					a.totalFrames > 0
						? Math.round((a.droppedFrames / a.totalFrames) * 1000) / 10
						: null
			});
		} catch {
			// Telemetry must never break playback.
		}
	}

	// -------------------------------------------------------------------------
	// Element listeners — shared by all pipelines. useEventListener rebinds
	// automatically when the ref changes (iOS shared element claim/release).
	// -------------------------------------------------------------------------

	useEventListener(options.videoElement, 'playing', () => {
		if (!attempt) return;
		// Playback can continue — but nothing is painted yet. Only ends a stall
		// and (re)arms the frame confirmation; the frame itself is confirmed by
		// requestVideoFrameCallback, or by the guarded timeupdate fallback.
		closeStall(attempt);
		armFrameConfirmation();
	});

	// The element arrives after the attempt on every pipeline (nextTick in the
	// feed, claim/release on iOS) — arm as soon as it exists.
	watch(options.videoElement, () => armFrameConfirmation());

	useEventListener(options.videoElement, 'timeupdate', () => {
		if (!attempt) return;
		const video = options.videoElement.value;
		if (!video) return;
		// The paused/readyState guard rejects stray events from a shared element
		// that is still showing the PREVIOUS reel (iOS claim/release window).
		const isPlayingNow =
			video.currentTime > 0 &&
			!video.paused &&
			video.readyState >= HAVE_CURRENT_DATA;

		if (isPlayingNow) {
			// timeupdate is only a FALLBACK first-frame signal. Where rVFC exists
			// it is the authority: timeupdate fires on media-time progress, which
			// can beat the first presented frame and would report a startup that
			// never happened on screen.
			if (attempt.firstFrameAt === null && !supportsFrameCallback(video)) {
				onFirstFrame();
			}
			// Advancing media time proves playback resumed, whatever confirmed
			// the frame — safe to close a stall on both paths.
			if (attempt.firstFrameAt !== null) closeStall(attempt);
		}
		// Snapshot progress while the element is verifiably ours — finalize()
		// must not touch the element (see Attempt comment).
		if (attempt.firstFrameAt !== null) {
			// Forward deltas of currentTime accumulate true watch time even on
			// looped/replayed playback; seeks and loop-wraps fall outside
			// (0, MAX_PLAY_DELTA_S) and are ignored.
			const t = video.currentTime;
			if (attempt.lastMediaTime !== null) {
				const delta = t - attempt.lastMediaTime;
				if (delta > 0 && delta < MAX_PLAY_DELTA_S) {
					attempt.playedSeconds += delta;
				} else if (delta < 0) {
					// Time went backwards: loop wrap (cards play with `loop`) or a
					// seek. Either way the buffer refill that may follow is not
					// user-perceived rebuffering — grace it like a seek.
					attempt.lastSeekAt = now();
					attempt.stallStartedAt = null;
				}
			}
			attempt.lastMediaTime = t;

			const duration = video.duration;
			if (duration && isFinite(duration) && duration > 0) {
				attempt.durationSeconds = duration;
			}
			if (
				attempt.framesBaseline &&
				typeof video.getVideoPlaybackQuality === 'function'
			) {
				try {
					const q = video.getVideoPlaybackQuality();
					attempt.droppedFrames = Math.max(
						0,
						q.droppedVideoFrames - attempt.framesBaseline.dropped
					);
					attempt.totalFrames = Math.max(
						0,
						q.totalVideoFrames - attempt.framesBaseline.total
					);
				} catch {
					// Keep the last good snapshot.
				}
			}
		}
	});

	useEventListener(options.videoElement, 'waiting', () => {
		if (!attempt || attempt.firstFrameAt === null) return;
		if (now() - attempt.lastSeekAt < SEEK_GRACE_MS) return;
		if (attempt.stallStartedAt === null) attempt.stallStartedAt = now();
	});

	useEventListener(options.videoElement, 'seeking', () => {
		if (!attempt) return;
		attempt.lastSeekAt = now();
		// A stall interrupted by a user seek is no longer a network stall.
		attempt.stallStartedAt = null;
	});

	useEventListener(options.videoElement, 'ended', () => finalize('ended'));

	onBeforeUnmount(() => finalize('navigated'));

	return { beginAttempt, finalize, abort };
}
